import React, { useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Dialog, DialogContent, DialogTitle } from "@mui/material";
import Markdown from "react-markdown";
import { useQuery } from "@apollo/client";
import { FETCH_DOCUMENT_BY_REFERENCE } from "@documents/graphql/queries/fetchDocumentByReference";
import { ContentNode } from "@/app/graphql/codegen";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import DocChip from "./DocChip";
import PDFViewer from "@documents/components/PDFViewer";
import { getUnifiedDocumentLabel } from "@/utils/documentLabels";

interface DocumentDialogProps {
    /**
     * Bestimmt, ob der Dialog geöffnet ist oder nicht
     */
    open: boolean;

    /**
     * Funktion, die aufgerufen wird, wenn der Dialog geschlossen wird
     */
    onClose: () => void;

    /**
     * Das ContentNode-Objekt, das angezeigt werden soll
     * Falls dieses gesetzt ist, wird der Inhalt direkt aus dem 'text' Feld verwendet
     */
    contentNode?: ContentNode | null;

    /**
     * Referenz des Dokuments, falls statt einer ContentNode ein Dokument gezeigt werden soll
     * Es wird
     */
    documentReference?: string;

    /**
     * Zusätzliche Informationen zum Dokument, die im Titel angezeigt werden
     */
    additionalInfo?: string;

    /**
     * Seitenreferenz, zu der gescrollt werden soll
     */
    page?: string;

    /**
     * Optionaler Titel für den Dialog
     */
    title?: string;
}

const DocumentDialog: React.FC<DocumentDialogProps> = ({ open, onClose, contentNode, documentReference, additionalInfo, page, title }) => {
    const [documentContent, setDocumentContent] = useState<string | undefined>(contentNode?.text);

    const [effectivePdfUrl, setEffectivePdfUrl] = useState<string | undefined>(undefined);

    const [isLoading, setIsLoading] = useState(false);
    const contentRef = useRef<HTMLDivElement>(null);

    // Ermittle einen konsistenten Anzeigetitel mit unserer einheitlichen Funktion, wenn contentNode verfügbar ist,
    // ansonsten baue ihn aus einzelnen Teilen auf
    const displayTitle =
        title ||
        (contentNode
            ? getUnifiedDocumentLabel(contentNode, { showParentPrefix: true })
            : documentReference
              ? `${documentReference}${additionalInfo ? ` - ${additionalInfo}` : ""}${page && page !== "1" ? ` - ${page}` : ""}`
              : "Dokument");

    // Wenn ein Dokument gezeigt werden soll, wird der Inhalt aus der Datenbank geladen
    const { loading: documentLoading } = useQuery(FETCH_DOCUMENT_BY_REFERENCE, {
        variables: { reference: documentReference || "" },
        skip: !documentReference,
        onCompleted: (data) => {
            if (data?.officialDocumentByReference) {
                setDocumentContent(data.officialDocumentByReference.rootContentNode.text);

                setEffectivePdfUrl(data.officialDocumentByReference.pdfFileUrl);

                // Nach dem Laden des Inhalts zur entsprechenden Seite scrollen
                setTimeout(() => scrollToPage(), 100);
            }
        },
    });

    useEffect(() => {
        setIsLoading(documentLoading);
    }, [documentLoading]);

    useEffect(() => {
        // ContentNode hat direkt Vorrang, wenn es gesetzt ist
        if (contentNode?.text) {
            setDocumentContent(contentNode.text);
        }
    }, [contentNode]);

    useEffect(() => {
        if (open && page) {
            setTimeout(() => scrollToPage(), 100);
        }
    }, [open, page, documentContent]);

    const scrollToPage = () => {
        if (contentRef.current && page) {
            console.log("Attempting to scroll to page:", page);

            // Seitennummer aus Formaten wie "S. 1" oder "S. 3-4" oder "1" extrahieren
            // Zuerst versuche, aus Formaten wie "S. 1", "S.1", "S1" zu extrahieren
            let pageMatch = page.match(/S\.?\s*(\d+)(?:[^\d]|$)/i);

            // Falls nicht gefunden, suche einfach nach einer beliebigen Zahl
            if (!pageMatch) {
                pageMatch = page.match(/(\d+)/);
            }

            const pageNumber = pageMatch ? pageMatch[1] : null;

            console.log("Extracted page number:", pageNumber);

            if (pageNumber) {
                // Nach Seitenmarkierungen im Dokument suchen
                const allParagraphs = contentRef.current.querySelectorAll("p");
                let pageElement = null;

                // Paragraph finden, der genau "Page X" enthält
                for (const paragraph of allParagraphs) {
                    if (paragraph.textContent?.trim() === `Page ${pageNumber}`) {
                        pageElement = paragraph;
                        console.log(`Found exact page match: Page ${pageNumber}`);
                        break;
                    }
                }

                // Wenn keine exakte Übereinstimmung gefunden wurde, lockere Übereinstimmung versuchen
                if (!pageElement) {
                    for (const paragraph of allParagraphs) {
                        if (paragraph.textContent?.includes(`Page ${pageNumber}`)) {
                            pageElement = paragraph;
                            console.log(`Found partial page match: ${paragraph.textContent}`);
                            break;
                        }
                    }
                }

                if (pageElement) {
                    pageElement.scrollIntoView({ behavior: "smooth", block: "start" });
                    console.log(`Scrolled to page ${pageNumber}`);
                } else {
                    console.log(`Could not find page ${pageNumber} in document`);
                    if (additionalInfo) {
                        // Alternativ versuchen, einen Abschnitt basierend auf additionalInfo zu finden
                        const sectionElement = contentRef.current.querySelector(`h1, h2, h3, h4, h5, h6, p, li`);

                        if (sectionElement) {
                            sectionElement.scrollIntoView({ behavior: "smooth", block: "start" });
                            console.log(`Scrolled to section based on additionalInfo: ${additionalInfo}`);
                        } else {
                            console.log(`Could not find section based on additionalInfo: ${additionalInfo}`);
                        }
                    }
                }
            } else {
                console.log("Could not extract page number from:", page);
            }
        } else {
            console.log("Cannot scroll to page - missing reference or page:", { contentRef: !!contentRef.current, page });
        }
    };

    const extractPageNumber = (raw: string | undefined): number | undefined => {
        if (!raw) return undefined;

        const match = raw.match(/S\.?\s*(\d+)/i) || raw.match(/(\d+)/);
        return match ? parseInt(match[1], 10) : undefined;
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl">
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                    {(contentNode || documentReference) && (
                        <Box sx={{ mr: 1 }}>
                            <DocChip
                                node={contentNode ? contentNode : ({ associatedOfficialDocument: { reference: documentReference! }, metadata: {}, type: "SUMMARY" } as ContentNode)}
                                showTooltip={false}
                            />
                        </Box>
                    )}
                    {/*<span>{displayTitle}</span>*/}
                </Box>
                <IconButton onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pl: 5, pr: 7 }}>
                {isLoading || documentLoading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                        <CircularProgress />
                    </Box>
                ) : effectivePdfUrl ? (
                    <PDFViewer
                        url={effectivePdfUrl}
                        page={extractPageNumber(page)} // z.B. 3
                        height="740px"
                        width="100%"
                    />
                ) : (
                    <Box ref={contentRef}>
                        {documentContent && (
                            <Markdown
                                components={{
                                    p: ({ children, ...props }) => {
                                        // Überprüfen, ob dieser Paragraph ein Seitenindikator ist
                                        if (
                                            React.Children.count(children) === 1 &&
                                            typeof React.Children.toArray(children)[0] === "string" &&
                                            /^Page \d+$/.test(React.Children.toArray(children)[0] as string)
                                        ) {
                                            // Dies ist ein Seitenindikator, visuell verstecken aber im DOM belassen
                                            return (
                                                <p
                                                    {...props}
                                                    id={`page-${(React.Children.toArray(children)[0] as string).replace("Page ", "")}`}
                                                    style={{
                                                        position: "relative",
                                                        height: "1px",
                                                        margin: 0,
                                                        padding: 0,
                                                        opacity: 0,
                                                        overflow: "hidden",
                                                    }}
                                                >
                                                    {children}
                                                </p>
                                            );
                                        }

                                        // Normaler Paragraph
                                        return <p {...props}>{children}</p>;
                                    },
                                }}
                            >
                                {documentContent}
                            </Markdown>
                        )}
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default DocumentDialog;
